import type { IgAccount, TaskWithContact } from "@ml/shared";
import { Avatar, Badge, Button, Card, EmptyState, Skeleton, Tooltip, buttonClass, cx, useToast } from "@ml/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight, CalendarCheck, Check, ListChecks, RefreshCw } from "lucide-react";
import { Link } from "react-router";
import { IgAccountsStrip } from "../components/accounts/IgAccountsStrip";
import { EngineBadge, engineSentence } from "../components/EngineBadge";
import { ExtensionGlyph } from "../components/IgGlyph";
import { NoIgAccount } from "../components/NoIgAccount";
import { HomeAnalytics } from "./HomeAnalytics";
import { ImpactPanel } from "../features/analytics/impact/ImpactPanel";
import "./home-dashboard.css";
import { ApiError, crmTasksApi } from "../lib/api";
import { useUser } from "../lib/auth";
import { firstName, formatCount, formatDate, formatTime, greeting, relativeTime } from "../lib/format";
import { useDocumentTitle } from "../lib/hooks";
import { useIgAccount } from "../lib/igAccount";
import { useExtensionStatus } from "../lib/sso";

export function HomePage() {
  useDocumentTitle("Dashboard");
  const user = useUser();
  const { current, isLoading } = useIgAccount();
  const today = new Intl.DateTimeFormat(undefined, { weekday: "long", month: "long", day: "numeric" }).format(new Date());

  return (
    <div className="gfp-page gfp-home hd-page">
      <header className="gfp-home__hello">
        <p className="gfp-home__date">{today}</p>
        <h1 className="gfp-home__greeting">
          Your growth dashboard
        </h1>
        <p className="gfp-home__sub">
          {current ? (
            <>{greeting()}, {firstName(user.name)}. Here’s how @{current.username} is growing.</>
          ) : (
            <>Let's get your first LinkedIn account connected.</>
          )}
        </p>
      </header>

      {/* Two or more LinkedIn accounts: one small card each; click to switch workspaces. */}
      <IgAccountsStrip />

      {/* Growth since the first scan + the hours (and dollars) Magically Linked has saved. */}
      {current ? <ImpactPanel account={current} /> : null}

      <ExtensionCard />
      {isLoading ? <Skeleton height={360} radius={10} /> : current ? (
        <HomeAnalytics key={current.id} account={current} />
      ) : <Card padding="none"><NoIgAccount compact /></Card>}
      <div className="hd-bottom">
        <NextMoves igAccountId={current?.id ?? null} />
        {current ? <AccountCard account={current} /> : null}
      </div>
    </div>
  );
}

function AccountCard({ account }: { account: IgAccount }) {
  return (
    <Card accent className="gfp-account">
      <div className="gfp-account__id">
        <Avatar src={account.profilePicUrl} name={account.fullName || account.username} size={64} ring />
        <div className="gfp-account__names">
          <a className="gfp-account__handle" href={`https://www.linkedin.com/${encodeURIComponent(account.username)}/`} target="_blank" rel="noreferrer">
            @{account.username}
            <ArrowUpRight size={15} aria-hidden="true" />
            <span className="gf-sr-only">(opens LinkedIn)</span>
          </a>
          {account.fullName ? <span className="gfp-account__full">{account.fullName}</span> : null}
          <span className="gfp-account__badges">
            <EngineBadge status={account.engineStatus} size="sm" />
            {account.isBusiness ? <Badge size="sm">Business</Badge> : null}
            {account.isPrivate ? <Badge size="sm" tone="warn">Private</Badge> : null}
          </span>
        </div>
      </div>
      <dl className="gfp-account__stats">
        <div>
          <dt>Followers</dt>
          <dd>{formatCount(account.followers)}</dd>
        </div>
        <div>
          <dt>Following</dt>
          <dd>{formatCount(account.following)}</dd>
        </div>
        <div>
          <dt>Posts</dt>
          <dd>{formatCount(account.posts)}</dd>
        </div>
      </dl>
      <p className="gfp-account__engine">{engineSentence(account.engineStatus)}</p>
    </Card>
  );
}

function ExtensionCard() {
  const ext = useExtensionStatus();
  const connected = ext.status === "connected";
  return (
    <Card className={cx("gfp-extcard", `gfp-extcard--${ext.status}`)}>
      <div className="gfp-extcard__icon" aria-hidden="true">
        <ExtensionGlyph size={22} />
      </div>
      <h2 className="gfp-extcard__title">
        {connected
          ? "Extension connected"
          : ext.status === "signed_out"
            ? "Extension needs you to sign in"
            : ext.status === "checking"
              ? "Looking for the extension…"
              : "Extension not detected"}
      </h2>
      <p className="gfp-extcard__text">
        {connected
          ? `Signed in to this browser${ext.version ? ` • v${ext.version}` : ""}. Automation follows your account’s settings and active hours.`
          : ext.status === "signed_out"
            ? "It's installed but signed out. Connect it to this account in one click."
            : ext.status === "checking"
              ? "One second."
              : "The extension connects your signed-in LinkedIn browser session. This preview supports the workflows listed in Help → Magically Linked development preview."}
      </p>
      <div className="gfp-extcard__actions">
        {ext.status === "signed_out" ? (
          <Button variant="primary" size="sm" loading={ext.syncing} onClick={() => void ext.connect()}>
            Connect now
          </Button>
        ) : !connected && ext.status !== "checking" ? (
          <Link to="/connect-extension" className={buttonClass({ variant: "primary", size: "sm" })}>
            Set up the extension
          </Link>
        ) : (
          <Link to="/connect-extension" className={buttonClass({ variant: "ghost", size: "sm" })}>
            Extension details
          </Link>
        )}
      </div>
    </Card>
  );
}

function dueLabel(t: TaskWithContact): { text: string; tone: "danger" | "accent" | "neutral" } {
  if (!t.dueAt) return { text: "No due date", tone: "neutral" };
  const now = new Date();
  const due = new Date(t.dueAt);
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime();
  if (t.dueAt < now.getTime()) return { text: `Due ${relativeTime(t.dueAt)}`, tone: "danger" };
  if (t.dueAt < endOfToday) return { text: `Today ${formatTime(t.dueAt)}`, tone: "accent" };
  return { text: formatDate(due.getTime(), { weekday: "short", month: "short", day: "numeric" }), tone: "neutral" };
}

function NextMoves({ igAccountId }: { igAccountId: string | null }) {
  const qc = useQueryClient();
  const toast = useToast();
  // Key lives under "crm" so the CRM feature's invalidations refresh this list too.
  const key = ["crm", "tasks", { status: "open", igAccountId }] as const;
  const q = useQuery({
    queryKey: key,
    queryFn: async () => (await crmTasksApi.list({ status: "open", igAccountId: igAccountId ?? undefined })).items,
    enabled: !!igAccountId,
    retry: false,
  });

  const complete = useMutation({
    mutationFn: (id: string) => crmTasksApi.update(id, { done: true }),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<TaskWithContact[]>(key);
      qc.setQueryData<TaskWithContact[]>(key, (old) => old?.filter((t) => t.id !== id));
      return { prev };
    },
    onError: (err, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      toast.error(err);
    },
    onSuccess: () => toast.success("Done. One less thing."),
    onSettled: () => void qc.invalidateQueries({ queryKey: ["crm", "tasks"] }),
  });

  // The CRM tasks endpoint may not be deployed yet — treat "not there" as "nothing to do".
  const unavailable = q.error instanceof ApiError && (q.error.status === 404 || q.error.status === 501);
  const tasks = (q.data ?? [])
    .slice()
    .sort((a, b) => (a.dueAt ?? Number.MAX_SAFE_INTEGER) - (b.dueAt ?? Number.MAX_SAFE_INTEGER))
    .slice(0, 8);

  return (
    <Card
      title="Today's next moves"
      subtitle="Your growth actions and follow-ups, soonest first."
      padding="none"
      actions={
        <Link to="/crm/tasks" className={buttonClass({ variant: "ghost", size: "sm" })}>
          Plan with AI
        </Link>
      }
    >
      {!igAccountId ? (
        <EmptyState size="sm" icon={<ListChecks />} title="Nothing to chase yet" description="Connect a LinkedIn account and your follow-ups show up here." />
      ) : q.isPending ? (
        <ul className="gfp-moves" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <li key={i} className="gfp-moves__item">
              <Skeleton circle height={20} />
              <Skeleton height={12} width={`${50 + i * 12}%`} />
            </li>
          ))}
        </ul>
      ) : q.isError && !unavailable ? (
        <EmptyState
          size="sm"
          icon={<RefreshCw />}
          title="Couldn't load your follow-ups"
          description={q.error instanceof Error ? q.error.message : undefined}
          action={
            <Button size="sm" variant="secondary" onClick={() => void q.refetch()}>
              Try again
            </Button>
          }
        />
      ) : tasks.length === 0 ? (
        <EmptyState
          size="sm"
          icon={<CalendarCheck />}
          title="Clean slate"
          description={
            unavailable
              ? "Follow-up tasks from your pipeline land here once the CRM is switched on."
              : "Open Plan with AI to generate next moves for growth, leads, and follow-ups."
          }
        />
      ) : (
        <ul className="gfp-moves">
          {tasks.map((t) => {
            const due = dueLabel(t);
            return (
              <li key={t.id} className="gfp-moves__item">
                <Tooltip content="Mark done">
                  <button
                    type="button"
                    className="gfp-moves__check"
                    aria-label={`Mark "${t.title}" done`}
                    onClick={() => complete.mutate(t.id)}
                    disabled={complete.isPending && complete.variables === t.id}
                  >
                    <Check size={13} strokeWidth={3} />
                  </button>
                </Tooltip>
                <div className="gfp-moves__body">
                  <span className="gfp-moves__title">{t.title}</span>
                  {t.contact ? (
                    <span className="gfp-moves__contact">
                      <Avatar src={t.contact.profilePicUrl} name={t.contact.fullName || t.contact.username} size={18} />@{t.contact.username}
                    </span>
                  ) : null}
                </div>
                <Badge size="sm" tone={due.tone === "neutral" ? "neutral" : due.tone}>
                  {due.text}
                </Badge>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
